import { useEffect, useMemo } from 'react'

import { useReducer } from '@cash-web/shared-data-access-redux-inspect'
import { createSingleUseFlowToken } from '@cash-web/shared-util-fetch'

import { isFlowComplete } from '../utils'
import { flowReducer, FlowState } from './flowReducer'
import { useFlowLogging } from './useFlowLogging'
import {
  clientScenarioLogger,
  useBlockerSubmitSideEffect,
  useBlockerViewSideEffect,
  useFlowCancelSideEffect,
  useFlowCompleteSideEffect,
  UseFlowReducerProps,
  useFlowResponseSideEffect,
  useFlowStartSideEffect,
} from './useFlowSideEffects'

export const defaultFlowState = (): FlowState => ({
  suspended: {},
  blockerDescriptors: [],
  skippedBlockers: [],
  path: [],
  isInProgress: false,
  isLoading: false,
  flowToken: createSingleUseFlowToken(),
  initiatorRequestContext: undefined,
})

const getFlowReducerName = (state: FlowState) => `[${state.trackingFlowName}] useFlowReducer`
export const useFlowReducer = ({
  initialState,
  onDismissAction,
  onCompleteFlow,
  onCancelFlow,
  onBlockerView,
  onFlowResponse,
}: UseFlowReducerProps) => {
  const { logStartFlow } = useFlowLogging()

  // side effects
  const flowStartSideEffect = useFlowStartSideEffect()
  const flowCompleteSideEffect = useFlowCompleteSideEffect({ onCompleteFlow })
  const flowCancelSideEffect = useFlowCancelSideEffect({ onCancelFlow, onDismissAction })
  const blockerViewSideEffect = useBlockerViewSideEffect({ onBlockerView })
  const blockerSubmitSideEffect = useBlockerSubmitSideEffect()
  const flowResponseSideEffect = useFlowResponseSideEffect({ onFlowResponse })

  const combinedInitialState = useMemo(
    () => ({
      ...defaultFlowState(),
      ...initialState,
    }),
    [initialState]
  )

  const [state, dispatch, subscribe] = useReducer(
    flowReducer,
    combinedInitialState,
    (initialState: FlowState) => {
      if (initialState.trackingFlowName) {
        logStartFlow(initialState)
        initialState = {
          ...initialState,
          flowStartTime: Date.now(),
          isInProgress: !isFlowComplete(initialState),
        }
      }
      return initialState
    },
    { name: getFlowReducerName }
  )

  useEffect(() => {
    return subscribe(clientScenarioLogger)
  }, [subscribe])

  useEffect(() => {
    return subscribe(flowStartSideEffect)
  }, [subscribe, flowStartSideEffect])

  useEffect(() => {
    return subscribe(flowCompleteSideEffect)
  }, [subscribe, flowCompleteSideEffect])

  useEffect(() => {
    return subscribe(flowCancelSideEffect)
  }, [subscribe, flowCancelSideEffect])

  useEffect(() => {
    return subscribe(blockerViewSideEffect)
  }, [subscribe, blockerViewSideEffect])

  useEffect(() => {
    return subscribe(blockerSubmitSideEffect)
  }, [subscribe, blockerSubmitSideEffect])

  useEffect(() => {
    return subscribe(flowResponseSideEffect)
  }, [subscribe, flowResponseSideEffect])

  return { state, dispatch }
}
